/**
 * VoiceEngine — the portable voice-conversation brain: state machine (idle|listening|thinking|
 * speaking), barge-in policy (AEC-trivial + heuristic echo tier), gapless TTS turn handoff,
 * utterance merge window, interrupted-reply tracking, ack echo-leak guard.
 *
 * Pure logic over three injected seams (STT, TTS, AudioSink) — runs in CLI, server, or browser.
 * Hosts wire `onUtterance` to their dispatch and feed `beginSpeech()/speakDelta()/endSpeech()`;
 * `interrupt()` is the barge-in primitive. Behavior validated live; see mind/11-voice.md.
 */
import { forComponent } from '../logging';
import { EmotionStream } from './emotion';
import type { AudioSink, VoiceState } from './types';

const log = forComponent('VoiceEngine');
const now = () => performance.now();

/** Sanitize text for the spoken channel (and its on-screen echo). Drops markdown punctuation TTS would
 *  read aloud ("star"/"hash"), turns em/en dashes and table pipes into spoken pauses, normalizes "30 %"
 *  → "30%", and collapses filler dot-runs ("now....." → "now."). Per-delta safe: every map is local to
 *  the chunk (single chars / line-anchored), so chunk boundaries never split a multi-char span. */
export const forSpeech = (t: string): string =>
  t.replace(/[*_`#]+/g, '')
   .replace(/^[ \t]*[-•]\s+/gm, '')        // leading list marker
   .replace(/\s*[\u2013\u2014]\s*/g, ', ')  // en/em dash → spoken pause (prompt forbids dashes in speech)
   .replace(/[\u2010\u2011]/g, '-')          // (non-breaking) hyphen → plain hyphen (TTS reads "8-12" fine)
   .replace(/\s*\|\s*/g, ', ')             // table pipe → pause
   .replace(/(\d)\s+%/g, '$1%')       // "30 %" → "30%" (else "thirty space percent")
   .replace(/\.{3,}/g, '.');          // filler dot-run → single period

/** Structural contracts (satisfied by SonioxSTT/CartesiaTTS or test fakes). */
export interface SttLike {
  usingAec: boolean;
  onPartial: (text: string) => void;
  onUtterance: (text: string, endpointAt: number) => void;
  onLevel: (rms: number) => void;
  /** Optional: unrecoverable capture failure (e.g. mic produced no audio) — host tears voice down. */
  onFatal?: (message: string) => void;
  start(): Promise<void> | void;
  reset(): void;
  stop(): void;
}
export interface TtsLike {
  onAudio: (chunk: Uint8Array) => void;
  onDone: () => void;
  connect(): Promise<void> | void;
  newContext(): string;
  speak(text: string, cont: boolean): void;
  end(): void;
  cancel(): void;
  close(): void;
}

export class VoiceEngineOptions {
  stt!: SttLike;
  tts!: TtsLike;
  player!: AudioSink;
  /** a final utterance arrived (endpoint) — host dispatches it as a turn */
  onUtterance: (text: string) => void = () => {};
  /** live partial transcript while listening (host renders the 🎤 line) */
  onPartial: (text: string) => void = () => {};
  onState: (s: VoiceState) => void = () => {};
  /** user spoke/acted over playback — host aborts the in-flight turn (called AFTER audio is killed).
   *  phase: 'speaking' = cut mid-speech (real interruption); 'drain' = in the final audio tail
   *  (normal turn-taking — hosts shouldn't alarm). */
  onBargeIn: (phase: 'speaking' | 'drain') => void = () => {};
  /** spoken micro-ack on utterance endpoint (masks LLM TTFT); '' disables */
  ackPhrase = '';
  /** Endpoint merge window (ms): hold an endpointed utterance briefly — if speech resumes (spelled
   *  letters, mid-thought pauses), the next utterance MERGES instead of dispatching a truncated one
   *  ("E-L-Y." / "A."). Costs this much latency per turn; 0 disables. */
  utteranceMergeMs = 350;
  /** Extended merge window (ms) for utterances that look incomplete (trailing conjunction/filler).
   *  Gives the user time to finish their thought without triggering a model call. */
  incompleteMergeMs = 1500;
  /** Grace window (ms) after an utterance dispatches, during which the user's own trailing audio cannot
   *  barge the reply it requested. Soniox keeps finalizing partials past <end>; without this they read
   *  as a barge and abort the fresh turn (live: mid-sentence self-interruption + steps=1→steps=0 double
   *  abort). Short enough that a genuine immediate barge ("no wait—") still lands right after. */
  bargeGraceMs = 600;
  /** Barge-in (talk over the assistant to interrupt). true = full-duplex (needs echo cancellation, or
   *  the assistant's own TTS bleeds back and self-interrupts). false = HALF-DUPLEX: the engine is deaf
   *  while audible (speaking + drain tail), so echo can never become a phantom turn — the right mode
   *  when there's no AEC (e.g. the non-VPIO mic fallback) and no headphones. Cost: can't interrupt. */
  bargeIn = true;
  /** Filler phrase spoken when holding for an incomplete utterance ('' disables). */
  holdFiller = '';
  /** Called when the engine holds an incomplete utterance (host can render a visual cue). */
  onHold: () => void = () => {};
  /** heuristic (non-AEC) energy barge-in tuning */
  bargeRmsMult = 2;
  bargeRmsFloor = 500;
  /** Overlap turn-taking (AEC tier, needs player.pause/resume) — human phone-call model, driven by
   *  the STT ITSELF (a trained speech classifier) instead of energy thresholds (energy could not
   *  separate residue bursts from speech in every room — hiccup whack-a-mole): a GENUINE partial
   *  (novel words dominate — echo of our own reply is inert) while speaking → PAUSE (exact-sample
   *  hold); partial grows into dominant-novel ≥2 words → cede (interrupt; the LLM re-enters); partial
   *  stalls/endpoints without ceding (backchannel by DURATION, not vocabulary) → resume + drop. false disables. */
  overlapPause = true;
  /** no new partial activity for this long while paused → resume, drop the interjection */
  overlapResumeMs = 700;
  /** A genuine barge over a LONG reply is defeated by the dominant-novel gate: Meet echoes our own
   *  speech back, so the partial is mostly our words + a few of hers → never "dominant novel" → it
   *  resumes (replaying old audio — the audible "completes the buffer" blip) instead of ceding.
   *  Mechanism-based discriminator: a re-PAUSE this soon after a resume = a persistent human, not an
   *  echo blip (which pauses once and stalls). Cede on the re-pause regardless of the novel gate. */
  overlapRepauseCedeMs = 1500;
  /** Speculative ENERGY pre-pause while speaking (AEC tier): two residue gate-passes within 350ms →
   *  pause ~300ms before the STT tokens land. But energy CANNOT separate residue bursts from speech
   *  (the documented whack-a-mole) — so a residue spike during loud playback false-pauses with NO user
   *  speech at all, an audible hiccup. Default OFF: the genuine-gated STT partial is the
   *  mechanism-correct pause trigger; enable only if barge-in onset feels sluggish in a clean-AEC room. */
  overlapEnergyHold = false;
  /** Map inline `[emotion]` tags (emitted by the model, prompt-taught) into Cartesia inline emotion
   *  tags in the spoken transcript (sonic-3 stitches the prosody). false = strip them silently. */
  emotions = true;
  /** Show the `[emotion]` tags in the on-screen echo (debug). false = hide (spoken-only). */
  showEmotions = false;
}

export class VoiceEngine {
  public options: VoiceEngineOptions;
  public state: VoiceState = 'idle';
  protected stt: SttLike;
  protected tts: TtsLike;
  protected player: AudioSink;
  private speaking = false; // audible (deltas flowing OR audio draining)
  private ctxOpen = false; // the current TTS context still accepts deltas (false once end-frame sent)
  private interrupted = false; // barge-in latch: drop in-flight deltas until the next legitimate turn
  private spokeDeltas = false; // a TTS context is open for the current spoken turn
  private drainTimer: ReturnType<typeof setTimeout> | null = null;
  // heuristic tier state (inert under AEC) — frozen as validated in the experiment
  private echoWords = new Set<string>();
  private prevReply = '';
  private reply = '';
  private echoUntil = 0;
  private baseline = 0;
  private hot = 0;
  private suspectUntil = 0;
  private ackAt = 0; // when the micro-ack was spoken — its echo can leak before the AEC filter converges
  private bargeGraceUntil = 0; // no barge-in until this time — the user's OWN trailing audio (after the
  // utterance that JUST dispatched this turn) must not immediately re-interrupt the reply it requested.
  private pendingUtt = ''; // endpointed text held for the merge window
  private pendingTimer: ReturnType<typeof setTimeout> | null = null;
  private lastInterrupted: { full: string; heard: string } | null = null;
  // overlap (pause) tier state — AEC + pause-capable sinks only
  private pausedAt = 0;
  private lastResumeAt = 0; // when the overlap last resumed from a false alarm — a quick re-pause cedes
  private lastOverlapPartial = ''; // change-detection: only NEW partial text counts as activity
  private resumeTimer: ReturnType<typeof setTimeout> | null = null;
  private turnStartAt = 0; // timestamp when the current turn began (for TTFT logging)
  // Central speech queue (above the TTS context): complete worker utterances serialize into ONE
  // playback stream, one-at-a-time, never splicing into the live reflex's open utterance.
  private uttQueue: string[] = [];
  // Per-turn emotion-tag parser (reset on beginSpeech) — converts `[emotion]` → Cartesia inline tags
  // for TTS, tracks tag-free prose for echo discrimination, and surfaces display text for the screen.
  private emo: EmotionStream | null = null;

  constructor(options?: Partial<VoiceEngineOptions>) {
    this.options = { ...new VoiceEngineOptions(), ...options };
    const o = this.options;
    if (!o.stt || !o.tts || !o.player) throw new Error('VoiceEngine needs stt, tts and player (see cli/voice.ts VoiceIO for platform defaults)');
    this.stt = o.stt;
    this.tts = o.tts;
    this.player = o.player;
  }

  async start(): Promise<void> {
    this.tts.onAudio = (c) => { if (this.speaking) this.player.write(c); };
    this.stt.onPartial = (text) => this.handlePartial(text);
    this.stt.onUtterance = (text) => this.handleUtterance(text);
    this.stt.onLevel = (rms) => this.handleLevel(rms);
    await Promise.all([this.tts.connect(), this.stt.start()]);
    this.setState('listening');
    log.debug(`voice I/O up (${this.stt.usingAec ? 'AEC' : 'heuristic echo'} capture)`);
  }

  get usingAec(): boolean {
    return this.stt.usingAec;
  }

  /** Flip barge-in at runtime (e.g. the mic fell back to non-VPIO → go half-duplex so echo can't leak). */
  setBargeIn(on: boolean): void { this.options.bargeIn = on; }

  /** Show/hide the `[emotion]` debug tags in the echo (next turn's stream picks it up). */
  setShowEmotions(on: boolean): void { this.options.showEmotions = on; }

  private idleWaiters: (() => void)[] = [];
  private setState(s: VoiceState): void {
    if (this.state === s) return;
    this.state = s;
    this.options.onState(s);
    if (s !== 'speaking' && s !== 'thinking') {
      for (const r of this.idleWaiters.splice(0)) r();
    }
  }

  /** Resolve when the engine is no longer speaking (immediate if already idle). */
  awaitIdle(): Promise<void> {
    if (this.state !== 'speaking' && this.state !== 'thinking') return Promise.resolve();
    return new Promise((r) => this.idleWaiters.push(r));
  }

  // --- speaking side (host-driven) ---

  /** open a spoken turn (idempotent — safe from both onUtterance and first-delta paths).
   *  `ack` speaks the configured micro-ack as the context opener (utterance path only —
   *  masks LLM TTFT; re-voice turns begun by their first delta skip it). */
  beginSpeech(ack = false): void {
    if (this.speaking && this.ctxOpen) return;
    if (this.drainTimer) { clearTimeout(this.drainTimer); this.drainTimer = null; }
    this.interrupted = false; // a new turn re-arms speech
    // gapless handoff: a new turn while the previous one's audio drains keeps the SAME player
    // (markTurn would cut the tail) and just opens a fresh TTS context — Cartesia rejects deltas
    // on an ended context ("Context closed" 400, narration silently lost).
    this.resetOverlap(true); // a held pause must release — gapless handoff keeps the same player (no FLUSH)
    if (!this.speaking) this.player.markTurn();
    this.speaking = true;
    this.ctxOpen = true;
    this.spokeDeltas = false;
    this.reply = '';
    this.emo = this.options.emotions ? new EmotionStream(this.options.showEmotions) : null;
    this.echoWords = new Set(this.words(this.prevReply)); // previous reply's echo may still be in flight
    this.tts.newContext();
    if (ack && this.options.ackPhrase) { this.tts.speak(this.options.ackPhrase + ' ', true); this.spokeDeltas = true; this.ackAt = now(); }
    // TTFT is stamped at the real turn start (flushUtterance, when the user's utterance dispatches);
    // only fall back to here for a re-voice turn that has no preceding utterance.
    if (!this.turnStartAt) this.turnStartAt = now();
    this.setState('thinking');
  }
  /** Feed a spoken delta. Returns the on-screen echo text (emotion tags shown/hidden per config) so the
   *  host renders the SAME stream that was parsed for TTS — no second, state-doubling parse. */
  speakDelta(text: string): string {
    // After a barge-in the aborted turn's stream may keep delivering (re-voice turns have no abort
    // signal) — those deltas must NOT re-open speech. endSpeech()/beginSpeech() clear the latch.
    if (this.interrupted) return '';
    if (!this.speaking || !this.ctxOpen) this.beginSpeech();
    // Split the delta into spoken transcript (Cartesia emotion tags inline), tag-free prose (echo
    // discrimination), and screen-echo display. The stream buffers a tag split across chunk boundaries.
    const { speech, display, prose } = this.emo ? this.emo.feed(text) : { speech: text, display: text, prose: text };
    this.reply += prose;
    for (const w of this.words(this.reply)) this.echoWords.add(w);
    this.tts.speak(forSpeech(speech), true); // strip markdown punctuation so TTS doesn't read "dash"/"star"/"hash" aloud
    if (!this.spokeDeltas && this.turnStartAt) log.debug(`ttft: ${Math.round(now() - this.turnStartAt)}ms`);
    this.spokeDeltas = true;
    this.setState('speaking');
    return display;
  }
  /** close the spoken turn (idempotent); stays audible until ALL audio arrived AND playback drains */
  endSpeech(): void {
    this.interrupted = false; // the interrupted turn is over — next turn may speak
    if (!this.speaking) return;
    this.ctxOpen = false; // context ends now; audio keeps draining
    // Flush any tail the emotion parser held back (a dangling `[…` partial, or a pending tag) so the
    // last words aren't swallowed; the real audio flush is tts.end()'s cont=false below.
    if (this.emo) { const t = this.emo.flush(); this.emo = null; if (t.prose) this.reply += t.prose; if (t.speech) this.tts.speak(forSpeech(t.speech), true); }
    if (this.reply) this.prevReply = this.reply;
    // remain 'speaking' (barge-in armed, echo discrimination active) until audio actually finishes
    const settle = () => {
      if (this.ctxOpen) { this.drainTimer = null; return; } // a newer turn took over the player — it settles
      if (this.pausedAt) { this.drainTimer = setTimeout(settle, 250); return; } // held by an overlap — not finished audibly
      this.drainTimer = null;
      this.speaking = false;
      if (this.turnStartAt) log.debug(`turn: ${Math.round(now() - this.turnStartAt)}ms (incl. playback)`);
      this.echoUntil = now() + 2500; // tail echo (capture+network+finalization) outlives playback
      if (!this.usingAec) this.stt.reset(); // drop echo residue (AEC: keep — it can only be the user)
      this.setState('listening');
      if (this.uttQueue.length) this.pumpQueue(); // next queued worker utterance plays after this one fully ends
    };
    const drainThenSettle = () => {
      if (this.drainTimer) clearTimeout(this.drainTimer);
      this.drainTimer = setTimeout(settle, this.player.drainMs() + 300);
    };
    if (this.spokeDeltas) {
      // the LLM turn is over but the TTS is still synthesizing — wait for ALL chunks (onDone)
      // before the drain countdown, else drainMs() undercounts and the reply's tail is dropped.
      this.tts.onDone = drainThenSettle;
      this.tts.end();
      // safety net: if 'done' never arrives (ws drop), settle after a generous ceiling
      if (this.drainTimer) clearTimeout(this.drainTimer);
      this.drainTimer = setTimeout(drainThenSettle, 15_000);
    } else drainThenSettle();
  }
  /** text of the reply cut by the last barge-in — consumed by the host to tell the model what
   *  the user did NOT hear. Cleared on read. */
  takeInterruptedReply(): { full: string; heard: string } | null {
    const r = this.lastInterrupted;
    this.lastInterrupted = null;
    return r;
  }

  /** Speak a short filler phrase without starting a model turn (stays in listening mode after). */
  speakFiller(text: string): void {
    if (!text || this.speaking) return;
    this.beginSpeech();
    this.speakDelta(text);
    this.endSpeech();
  }

  /** Enqueue a COMPLETE worker utterance (already-split spoken text) onto the central speech queue.
   *  If nothing is currently speaking it plays immediately; otherwise it queues and plays after the
   *  current utterance fully ends (settle → pumpQueue) — never spliced into an open reflex utterance. */
  enqueueUtterance(text: string): void {
    // Require speakable content (a letter/digit). A punctuation/quote-only utterance (e.g. a lone ")
    // produces no audio, so its own begin→end turn waits for a 'done' that never arrives and STALLS the
    // queue until the drain safety-net fires (~seconds of dead air). Drop it.
    if (!text || !/[\p{L}\p{N}]/u.test(text)) return;
    this.uttQueue.push(text);
    if (!this.speaking) this.pumpQueue();
  }
  /** Play the next queued worker utterance as its own one-shot turn (begin → delta → end). Drives the
   *  next one from the settle completion (endSpeech), so utterances serialize without overlap. */
  private pumpQueue(): void {
    if (this.speaking) return; // a turn is in flight — settle will re-pump
    const text = this.uttQueue.shift();
    if (text == null) return;
    this.beginSpeech();
    this.speakDelta(text);
    this.endSpeech();
  }

  /** barge-in: stop audio NOW, cancel generation, reset for the user's utterance */
  interrupt(): void {
    this.uttQueue = []; // barge-in drops any queued worker utterances (the user took the floor)
    if (!this.speaking && !this.drainTimer) return;
    if (this.drainTimer) { clearTimeout(this.drainTimer); this.drainTimer = null; }
    // estimate how much of the reply was actually heard: played-audio seconds ≈ spoken chars
    // (~15 chars/s of TTS speech) — coarse, but enough for "the user missed most of it".
    this.resetOverlap(false); // ceding — the flush below discards the held tape, don't resume it
    this.lastResumeAt = 0; // consume the re-pause signal: the next reply starts fresh
    const heardChars = Math.round((Math.max(0, this.player.playedMs()) / 1000) * 15); // paused time excluded by the sink
    if (this.reply) this.lastInterrupted = { full: this.reply, heard: this.reply.slice(0, heardChars) };
    this.speaking = false;
    this.ctxOpen = false;
    this.interrupted = true; // stragglers from the aborted stream stay silent
    this.suspectUntil = 0;
    // Meet's WebRTC echoes our audio back with ~1-2s network delay. The echo window must cover
    // the full playback duration + round-trip, not just a fixed tail. Without this, long responses
    // (stories, explanations) leak their echo tail past the window → false barge-in → cascade.
    this.echoUntil = now() + Math.max(2500, this.player.drainMs() + 3000);
    this.tts.cancel();
    this.player.kill();
    if (!this.usingAec) this.stt.reset(); // heuristic: drop the mixed echo/user buffer
    if (this.reply) this.prevReply = this.reply;
    this.setState('listening');
  }
  stop(): void {
    this.uttQueue = [];
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    if (this.pendingTimer) clearTimeout(this.pendingTimer);
    if (this.drainTimer) clearTimeout(this.drainTimer);
    this.stt.stop();
    this.player.kill();
    this.tts.close();
    this.setState('idle');
  }

  // --- listening side (STT-driven) ---

  private words(s: string): string[] {
    return s.toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter((w) => w.length >= 2);
  }
  private novelWords(text: string): string[] {
    return this.words(text).filter((w) => !this.echoWords.has(w));
  }
  private echoActive(): boolean {
    return this.speaking || now() < this.echoUntil;
  }
  /** Genuine user speech vs our own bleed (AEC tier): novel words must DOMINATE, not merely exist.
   *  Degraded AEC + an STT mis-hearing manufactures a single novel word out of pure echo (a name or
   *  rare word in our own reply comes back transcribed slightly differently — 1 novel / N words).
   *  A real interjection is mostly novel ("stop", "wait what") — short utterances pass on ratio,
   *  longer ones on count. */
  private genuine(text: string): boolean {
    const total = this.words(text).length;
    const novel = this.novelWords(text).length;
    // ratio, not count: a long mis-heard echo sentence yields 2-3 "novel" words out of a dozen
    // (live trace: "…latest email from you" from our "…latest news for you") — an absolute >=2
    // pass let those through. Real user speech is MOSTLY novel.
    return novel > 0 && novel / Math.max(1, total) > 0.5;
  }

  private handlePartial(text: string): void {
    if (this.speaking) {
      // Half-duplex (no AEC, no headphones): deaf while we speak — echo can't become a barge or partial.
      if (!this.options.bargeIn) return;
      // Barge grace: right after an utterance dispatched this turn, the user's OWN trailing audio is
      // still finalizing — it must not barge the reply they just requested (B3 over-interruption / B4
      // double-abort). Drop the barge/pause decision during the grace; still render the partial below.
      if (now() < this.bargeGraceUntil) {
        if (!this.echoActive() || (this.usingAec ? this.genuine(text) : this.novelWords(text).length >= 1)) this.options.onPartial(text);
        return;
      }
      if (this.overlapCapable) {
        // STT-driven overlap: Soniox saying "this is speech" is the discriminator (energy could not
        // separate residue from speech across rooms). New partial text → pause (trail-off); growing
        // into dominant-novel ≥2 words → cede; stalling → the resume timer releases the hold.
        const txt = text.trim();
        if (!txt || txt === this.lastOverlapPartial) return; // no NEW speech activity
        this.lastOverlapPartial = txt;
        // Echo gate on the PAUSE, not just the cede: our own audio bleeds back through imperfect AEC and
        // Soniox transcribes it as evolving partials. Pausing on each one (the old behavior) was an
        // audible hiccup mid-reply for every echo fragment — the "TTS interrupting itself" bug. genuine()
        // (novel words must DOMINATE) is the SAME speech/echo discriminator already used to cede; apply it
        // to the pause so pure echo is inert. A real barge is novel by definition → still pauses instantly,
        // even on one word ("stop" → 1 novel / 1 total → genuine).
        if (!this.genuine(txt)) { if (this.pausedAt) this.armResume(); return; } // echo: inert (keep a live hold ticking)
        if (!this.pausedAt) {
          this.pausedAt = now();
          this.player.pause!();
          // Re-pause right after a false-alarm resume → persistent human, not an echo blip. Cede now,
          // before the resume replays old audio again, and without waiting for the dominant-novel gate.
          if (this.lastResumeAt && now() - this.lastResumeAt < this.options.overlapRepauseCedeMs) {
            this.interrupt();
            this.options.onBargeIn(this.ctxOpen ? 'speaking' : 'drain');
            return;
          }
        }
        if (this.words(txt).length >= 2) { // genuine already true → dominant-novel ≥2 words → cede
          const phase = this.ctxOpen ? 'speaking' as const : 'drain' as const;
          this.interrupt();
          this.options.onBargeIn(phase);
          return;
        }
        this.armResume(); // genuine single word: hold; quiet releases
        return;
      }
      // Barge-in = NOVEL words (not in our own recent reply) — even with AEC. Cancellation quality
      // is a spectrum (VPIO convergence varies run to run); when it degrades, the assistant's own
      // bleed must not self-interrupt. User speech is novel by definition → still triggers fast.
      const barge = this.usingAec ? this.genuine(text) : this.novelWords(text).length >= (this.suspectUntil ? 1 : 2);
      if (barge) {
        const phase = this.ctxOpen ? 'speaking' as const : 'drain' as const;
        this.interrupt();
        this.options.onBargeIn(phase);
      }
      return;
    }
    if (this.pendingUtt && text.trim()) { // user kept talking during the merge window — wait for the next endpoint
      // RE-ARM (long fallback), never just clear: Soniox emits trailing partial frames after <end> —
      // clearing left the pending utterance parked forever (the user's barge words were LOST).
      if (this.pendingTimer) clearTimeout(this.pendingTimer);
      this.pendingTimer = setTimeout(() => this.flushUtterance(), Math.max(800, this.options.utteranceMergeMs));
    }
    // display partials unless they're echo-shaped during/after our own speech (degraded-AEC bleed)
    if (!this.echoActive() || (this.usingAec ? this.genuine(text) : this.novelWords(text).length >= 1)) this.options.onPartial(text);
  }

  /** Merge a resumed utterance into the pending one, deduping any word-overlap. Soniox re-finalizes
   *  overlapping audio when the silence-timer and the semantic `<end>` both endpoint a growing
   *  utterance (or after a reconnect): the next "utterance" repeats the tail of the previous one, and
   *  a naive `${prev} ${next}` produced the live duplication ("Um, I want to check if Um, I want to
   *  check if…"). Find the longest suffix of `prev`'s words that prefixes `next` and drop it. */
  private mergeUtterance(prev: string, next: string): string {
    if (!prev) return next;
    if (!next) return prev;
    const pw = prev.split(/\s+/), nw = next.split(/\s+/);
    const norm = (w: string) => w.toLowerCase().replace(/[^a-z0-9]/g, '');
    const max = Math.min(pw.length, nw.length);
    for (let k = max; k > 0; k--) {
      let match = true;
      for (let i = 0; i < k; i++) if (norm(pw[pw.length - k + i]) !== norm(nw[i])) { match = false; break; }
      if (match) return [...pw, ...nw.slice(k)].join(' ');
    }
    return `${prev} ${next}`;
  }

  private static readonly TRAIL_RE = /(?:^|\s)(?:and|but|or|so|to|the|a|an|of|in|for|with|that|if|uh|um|like|about|from|into|on|is|are|was|were|,)$/i;
  /** The utterance sounds like the user paused mid-thought (trailing conjunction/filler/comma). */
  private looksIncomplete(text: string): boolean {
    return VoiceEngine.TRAIL_RE.test(text.trim());
  }

  private handleUtterance(text: string): void {
    // Overlap that never grabbed the turn: duration said backchannel/noise → drop it. Real grabs
    // interrupted already (speaking=false). Applies while content flows (ctxOpen) AND while we are
    // holding a pause for THIS overlap (a drain-tail "yeah" leaked as a real turn otherwise). A
    // clean early answer during the drain — no hold in progress — still falls through (turn-taking).
    if (this.speaking && (this.ctxOpen || this.pausedAt) && this.overlapCapable) { this.stt.reset(); return; }
    // Echo-shaped utterances (no/few novel words while we are or just were audible) are dropped in
    // BOTH tiers — degraded AEC bleed otherwise becomes fake user turns ("🎤 › Friday.").
    // Half-duplex: drop EVERY utterance while audible (speaking + drain tail) — pure echo suppression.
    if (this.echoActive() && (!this.options.bargeIn || (this.usingAec ? !this.genuine(text) : this.novelWords(text).length < 2))) { this.stt.reset(); return; }
    // AEC converges over ~100ms at audio onset — the ack (first audio after silence) can leak back
    // as a transcript. Drop backchannel-shaped utterances shortly after we spoke it.
    const squash = (t: string) => t.toLowerCase().replace(/[^a-z]/g, '').replace(/(.)\1+/g, '$1');
    if (this.ackAt && now() - this.ackAt < 6000 && squash(text) === squash(this.options.ackPhrase)) { this.ackAt = 0; return; }
    // Merge window: semantic endpointing fires mid-spelling ("E-L-Y." … "A.") and mid-thought.
    // Hold briefly; speech resuming in the window appends to the SAME utterance. Substantial
    // utterances (≥4 words) skip the hold — they are never spelled fragments, and the saved 350ms
    // is the difference between ping-pong and lag in quick exchanges.
    this.pendingUtt = this.mergeUtterance(this.pendingUtt, text);
    if (this.pendingTimer) clearTimeout(this.pendingTimer);
    // Incomplete utterance hold: trailing conjunctions/fillers get an extended merge window + optional
    // filler phrase, letting the user finish their thought without triggering a model call.
    if (this.options.incompleteMergeMs && this.looksIncomplete(this.pendingUtt)) {
      log.verbose(`hold: incomplete utterance "${this.pendingUtt.slice(-40)}"`);
      this.options.onHold();
      if (this.options.holdFiller && !this.speaking) {
        this.beginSpeech();
        this.speakDelta(this.options.holdFiller);
        this.endSpeech();
      }
      this.pendingTimer = setTimeout(() => this.flushUtterance(), this.options.incompleteMergeMs);
      return;
    }
    if (!this.options.utteranceMergeMs || this.words(this.pendingUtt).length >= 4) return this.flushUtterance();
    this.pendingTimer = setTimeout(() => this.flushUtterance(), this.options.utteranceMergeMs);
  }
  private flushUtterance(): void {
    if (this.pendingTimer) { clearTimeout(this.pendingTimer); this.pendingTimer = null; }
    const text = this.pendingUtt;
    this.pendingUtt = '';
    if (text) {
      this.turnStartAt = now();
      // Trailing audio of the utterance we just dispatched keeps finalizing for a beat (Soniox emits
      // partials after <end>). Once the host opens a reply context (speaking=true), those self-tail
      // partials would read as a barge and abort the very reply the user asked for (the live "interrupts
      // mid-sentence" + double-abort steps=1→steps=0). Grace the barge path briefly after dispatch.
      this.bargeGraceUntil = now() + this.options.bargeGraceMs;
      this.options.onUtterance(text); // stamp the real turn start for TTFT
    }
  }

  private get overlapCapable(): boolean {
    return this.usingAec && this.options.overlapPause && !!this.player.pause && !!this.player.resume;
  }
  private armResume(): void {
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => {
      this.resumeTimer = null;
      if (!this.pausedAt) return; // (no speaking guard: a held pause must ALWAYS release, else the tape wedges)
      this.stt.reset(); // drop the stalled residue/backchannel buffer — it is not the next utterance
      this.resetOverlap(true); // the overlap died out (backchannel/noise) — pick up exactly where she left off
    }, this.options.overlapResumeMs);
  }
  private resetOverlap(resume: boolean): void {
    if (this.resumeTimer) { clearTimeout(this.resumeTimer); this.resumeTimer = null; }
    if (this.pausedAt && resume) { this.player.resume?.(); this.lastResumeAt = now(); }
    this.pausedAt = 0;
    this.lastOverlapPartial = '';
    this.gatePassTimes = [];
  }

  /** energy two-stage barge-in (heuristic tier only): spike over echo baseline → pause + confirm via STT */
  private gatePassTimes: number[] = []; // recent gate-PASSING chunks (helper zeroes residue — nonzero = vetted)
  private handleLevel(rms: number): void {
    if (this.usingAec) {
      // Fast hold trigger: a nonzero level while we are audible means the helper's gate PASSED it
      // (≥2× expected residue). Two passes within 350ms = speech onset → pause NOW (~300ms), without
      // waiting for STT tokens (which lag whenever the onset straddles the gate). The STT still owns
      // the DECISION: cede on genuine ≥2 words, or the resume timer releases the hold.
      if (!this.options.overlapEnergyHold || !this.speaking || !this.overlapCapable || this.pausedAt || rms < 50) return;
      const t = now();
      this.gatePassTimes = this.gatePassTimes.filter((x) => t - x < 350);
      this.gatePassTimes.push(t);
      if (this.gatePassTimes.length < 2) return;
      this.gatePassTimes = [];
      this.pausedAt = t;
      this.player.pause!();
      this.armResume();
      return;
    }
    if (!this.speaking) { this.baseline = 0; this.hot = 0; return; }
    // warm-up: let the EMA learn the echo level before triggers (else her own onset reads as a spike)
    if (!this.baseline) { this.baseline = rms; return; }
    this.baseline = this.baseline * 0.9 + rms * 0.1;
    if (rms > Math.max(this.baseline * this.options.bargeRmsMult, this.options.bargeRmsFloor)) this.hot++;
    else this.hot = 0;
    if (this.hot >= 2 && !this.suspectUntil) {
      this.suspectUntil = now() + 1300;
      setTimeout(() => { this.suspectUntil = 0; }, 1350);
    }
  }
}
